import { useZero } from "@rocicorp/zero/react";
import { keyframes, styled } from "goober";
import { nanoid } from "nanoid";
import { useMemo } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import type { StepTestDotStatus } from "../../shared/stepTests/freshness";
import { useQuery } from "../hooks/useQuery";
import { useStepTestExecutions } from "../hooks/useStepExecutions";
import { useStepTestStatusByDir } from "../hooks/useStepTestStatusByDir";
import { playClick } from "../lib/sounds";
import { stepDirsWithStepTests } from "../lib/stepTestDirs";
import { navigate, routes } from "../lib/urls";
import { Button } from "./Button";
import {
  StatusBreakdownChart,
  type StatusSegment,
} from "./StatusBreakdownChart";

type Props = {
  workflowId: string;
  branchId: string;
  onStepsHover?: (canonicalIds: ReadonlyArray<string> | null) => void;
  onAddTests?: () => void;
};

type TestBucket = "passed" | "failed" | "notrun";

const TEST_STATUS: Array<{ key: TestBucket; color: string; label: string }> = [
  { key: "passed", color: "var(--green-600)", label: "passing" },
  { key: "failed", color: "var(--red-600)", label: "failing" },
  { key: "notrun", color: "var(--gray-400)", label: "not checked" },
];

function bucketFor(status: StepTestDotStatus): TestBucket {
  if (status === "passed" || status === "failed") return status;
  return "notrun";
}

const Outer = styled("div")`
  height: 100%;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const RunTestsButton = styled(Button)`
  &.running > span:first-child {
    animation: ${spin} 1.5s linear infinite;
  }
`;

export function WorkflowStepTestsPanel({
  workflowId,
  branchId,
  onStepsHover,
  onAddTests,
}: Props) {
  const z = useZero();
  const [branch] = useQuery(queries.loadBranch({ workflowId, branchId }));
  const commitId = branch?.headCommit?.id ?? null;
  const commitFiles = useMemo(
    () => branch?.headCommit?.files ?? [],
    [branch?.headCommit?.files]
  );
  const stepDirs = useMemo(
    () => stepDirsWithStepTests(commitFiles),
    [commitFiles]
  );
  const { executions } = useStepTestExecutions(workflowId, branchId);
  const statusByDir = useStepTestStatusByDir({
    commitFiles,
    stepDirs,
    executions,
  });

  const running = executions.some(
    (e) => e.status === "pending" && stepDirs.includes(e.stepDir ?? "")
  );

  const handleRunAll = () => {
    if (!commitId || stepDirs.length === 0) return;
    for (const stepDir of stepDirs) {
      z.mutate(
        sharedMutators.runStepTests({
          runId: nanoid(12),
          workflowId,
          commitId,
          stepDir,
          executionId: nanoid(12),
        })
      );
    }
  };

  const segments = useMemo<StatusSegment[]>(() => {
    const dirsByBucket: Record<TestBucket, string[]> = {
      passed: [],
      failed: [],
      notrun: [],
    };
    for (const dir of stepDirs)
      dirsByBucket[bucketFor(statusByDir.get(dir) ?? "notrun")].push(dir);
    // "Not checked" only means something once a test actually is one; hide it at
    // zero, but keep passing/failing so the pass/fail framing always shows.
    return TEST_STATUS.map((d) => ({
      key: d.key,
      count: dirsByBucket[d.key].length,
      color: d.color,
      label: d.label,
      items: dirsByBucket[d.key],
    })).filter((s) => s.key !== "notrun" || s.count > 0);
  }, [stepDirs, statusByDir]);

  const barSegments = useMemo<StatusSegment[]>(() => {
    const byBucket: Record<TestBucket, string[]> = {
      passed: [],
      failed: [],
      notrun: [],
    };
    for (const dir of stepDirs)
      byBucket[bucketFor(statusByDir.get(dir) ?? "notrun")].push(dir);
    return TEST_STATUS.flatMap((d) =>
      byBucket[d.key].map((dir) => ({
        key: dir,
        count: 1,
        color: d.color,
        label: dir,
      }))
    );
  }, [stepDirs, statusByDir]);

  const handleSegmentClick = (seg: StatusSegment) => {
    playClick();
    onStepsHover?.(null);
    navigate(routes.space.workflow, {
      step: seg.key,
      file: "",
      view: "output",
    });
  };

  return (
    <Outer>
      <StatusBreakdownChart
        title="Tests"
        titleInfo="A step test captures a run’s input and expected output. Re-run it after edits to catch when the step stops producing that output."
        total={stepDirs.length}
        segments={segments}
        barSegments={barSegments}
        onSegmentHover={(seg) =>
          onStepsHover?.(seg ? (seg.items ?? [seg.key]) : null)
        }
        onSegmentClick={handleSegmentClick}
        accessory={
          stepDirs.length > 0 ? (
            <RunTestsButton
              variant="subtle"
              size="m"
              icon={running ? "refresh-cw" : "microscope"}
              className={running ? "running" : undefined}
              onClick={handleRunAll}
              disabled={running || !commitId}
            >
              Run tests
            </RunTestsButton>
          ) : onAddTests ? (
            <Button variant="subtle" size="m" icon="plus" onClick={onAddTests}>
              Add tests
            </Button>
          ) : undefined
        }
      />
    </Outer>
  );
}
